import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { FaIcon, faPlus, faArrowsRotate } from '@/lib/fa'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { formatTripTimespan } from '@/app/common/dateHelper'
import SearchColor from '@/assets/icons_new/SearchColor.svg'
import { getCountryFlag } from '@/app/services/country/countryFlagResolver'

interface BusinessTripEditAllowancesProps {
  tripData: any
  onUpdate: (field: string, value: any) => void
  onUpdateArrayItem?: (arrayName: string, index: number, updates: any) => void
  onAddArrayItem?: (arrayName: string, newItem: any) => void
  onRemoveArrayItems?: (arrayName: string, indices: number[]) => void
  companyId: string
  validationErrors?: Record<string, any>
  onGenerateAllowances?: () => void
}

const BusinessTripEditAllowances: React.FC<BusinessTripEditAllowancesProps> = ({
  tripData,
  onUpdate,
  companyId,
  onGenerateAllowances,
}) => {
  const { t } = useLocalize()

  const AllowancesColumns: TableHeaderColumn[] = [
    { title: t('businessTrip.col-country'), key: 'country', type: 'string', sort: false },
    { title: t('businessTrip.col-duration'), key: 'time', type: 'string', sort: false },
    { title: t('businessTrip.col-diet-count'), key: 'count', type: 'string', sort: false },
    { title: t('businessTrip.col-rate'), key: 'rate', type: 'string', sort: false },
    { title: t('businessTrip.col-amount'), key: 'amount', type: 'number', sort: false },
    { title: t('businessTrip.col-exchange-rate'), key: 'exchangeRate', type: 'number', sort: false },
    { title: t('businessTrip.col-table'), key: 'table', type: 'string', sort: false },
    { title: t('businessTrip.col-amount-pln'), key: 'plnAmount', type: 'number', sort: false },
  ]
  const [searchValue, setSearchValue] = React.useState('')
  const [sortConfig, setSortConfig] = React.useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)
  const [showSearch, setShowSearch] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  // Click outside to close search
  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setSearchValue('')
        setShowSearch(false)
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  const allowances = tripData?.allowances || []

  const filteredAllowances = React.useMemo(() => {
    if (!searchValue) return allowances
    const search = searchValue.toLowerCase()
    return allowances.filter((allowance: any) => {
      return (
        allowance.country?.name?.toLowerCase().includes(search) ||
        allowance.table?.toLowerCase().includes(search) ||
        allowance.rate?.toLowerCase().includes(search)
      )
    })
  }, [allowances, searchValue])

  const handleHeaderClick = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }

  const formatNumber = (value: number, precision: number = 2): string => {
    if (!value && value !== 0) return '-'
    const num = Number(value.toFixed(precision))
    if (num % 1 === 0) {
      return num.toString()
    }
    return parseFloat(num.toFixed(precision)).toString()
  }

  return (
    <div className="tw-h-full">
      <div className="tw-h-full tw-rounded-lg tw-bg-white tw-pb-4">
        {/* Action Bar */}
        <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-bg-[#fafbff] tw-px-4 tw-py-3">
          {/* Left side - Actions */}
          <div className="tw-flex tw-items-center tw-gap-4">
            {/* Search button with dropdown */}
            <div className="tw-relative" ref={containerRef}>
              <div
                className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-2 tw-px-1 ${
                  showSearch ? 'tw-rounded-lg tw-border-primary' : 'tw-border-transparent'
                }`}
                onClick={() => setShowSearch((prev) => !prev)}
              >
                <img src={SearchColor} alt="Search Icon" />
                <p className="tw-text-sm tw-font-medium tw-text-primary">
                  {t('operation.find') || 'Wyszukaj'}
                </p>
              </div>
              {showSearch && (
                <div className="tw-absolute tw-z-50 tw-mt-2 tw-w-[320px] tw-rounded-lg tw-bg-white tw-p-2 tw-shadow-md">
                  <div className="tw-relative tw-w-full">
                    <input
                      autoFocus
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      placeholder={t('operation.find') || 'Szukaj...'}
                      className="tw-w-full tw-rounded tw-border tw-border-neutral-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="tw-absolute tw-right-2 tw-top-1/2 tw-flex tw--translate-y-1/2 tw-items-center tw-gap-2">
                      {searchValue && (
                        <>
                          <span className="tw-text-sm tw-text-gray-500">
                            {filteredAllowances.length}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              setSearchValue('')
                            }}
                            className="tw-text-gray-400 hover:tw-text-gray-700"
                          >
                            ✕
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Add button */}
            <div
              className="tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6] tw-cursor-pointer"
              onClick={() => {
                // TODO: Implement add allowance functionality
              }}
              title={t('answer.add') || 'Dodaj'}
            >
              <FaIcon icon={faPlus} className="tw-h-4 tw-w-4 tw-text-[#2448bb]" />
              <span className="tw-text-sm tw-font-medium tw-text-[#2448bb]">
                {t('answer.add') || 'Dodaj'}
              </span>
            </div>

            {/* Generate allowances button */}
            <div
              className="tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6] tw-cursor-pointer"
              onClick={onGenerateAllowances}
              title={'Generuj diety'}
            >
              <FaIcon icon={faArrowsRotate} className="tw-h-4 tw-w-4 tw-text-[#2448bb]" />
              <span className="tw-text-sm tw-font-medium tw-text-[#2448bb]">
                Generuj diety
              </span>
            </div>
          </div>
        </div>

        {/* Allowances table */}
        <div className="tw-overflow-x-auto tw-rounded-lg tw-border tw-border-base-300">
          <table className="tw-w-full tw-table-auto tw-border-collapse">
            <TableHeader
              columns={AllowancesColumns}
              fontSize="md"
              isSelectedAny={false}
              rounded
              sortConfig={sortConfig}
              bolder
              disabledToggle={true}
              toggleAll={() => {}}
              onHeaderClick={handleHeaderClick}
            />
            <tbody>
              {filteredAllowances.length > 0 ? (
                filteredAllowances.map((allowance: any, index: number) => {
                  const plnAmount = (allowance.amount || 0) * (allowance.exchangeRate || 1)
                  return (
                    <tr key={index} className="hover:tw-bg-base-50">
                      <td className="tw-w-10" />
                      <TableTextRow
                        icon={
                          allowance.country?.code ? (
                            <img
                              src={getCountryFlag(
                                allowance.country.code.toUpperCase() === 'GB'
                                  ? 'EN'
                                  : allowance.country.code,
                              )}
                              width={18}
                              height={18}
                              alt=""
                              className="tw-mr-0"
                            />
                          ) : null
                        }
                      >{allowance.country?.name || '-'}</TableTextRow>
                      <TableTextRow>
                        {allowance.tripLenght ? formatTripTimespan(allowance.tripLenght) : '-'}
                      </TableTextRow>
                      <TableTextRow>{allowance.dietQuantity || '-'}</TableTextRow>
                      <TableTextRow>{allowance.rate || '-'}</TableTextRow>
                      <TableTextRow>{formatNumber(allowance.amount)}</TableTextRow>
                      <TableTextRow>{formatNumber(allowance.exchangeRate, 4)}</TableTextRow>
                      <TableTextRow>{allowance.table || '-'}</TableTextRow>
                      <TableTextRow>{formatNumber(plnAmount)}</TableTextRow>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={AllowancesColumns.length + 1} className="tw-p-8 tw-text-center">
                    <p className="tw-text-content-base tw-opacity-70">
                      {t('businessTrip.no-data') || 'Brak diet'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default BusinessTripEditAllowances

